import { StandardProps } from "@/types/types";
import styles from "./SimilarSearches.module.scss";
import { UnderlinedButton as V31UnderlinedButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { UnderlinedButton as DeprecatedUnderlinedButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { sanitiseQueryParams } from "@/utils/queryUtils";
import { dispatchLegacyAnalyticsEvent } from "@/utils/analytics";
import { ALSO_SEARCHED, GLOBAL_SEARCH_INTERACTION } from "@/utils/events";
import { getAnalyticsPageType } from "@/utils/setJLData";
import { useRouteParams } from "@/hooks/useRouteParams";
import { setAlsoSearched } from "@/utils/alsoSearched";
import { LinkWithoutPrefetch } from "../__shared__/LinkWithoutPrefetch/LinkWithoutPrefetch";
import { useFeatureFlags } from "@/flags/flags";

export function SimilarSearches({
  productListingData,
}: Pick<StandardProps, "productListingData">) {
  const route = useRouteParams();
  const { isJldsV31Enabled } = useFeatureFlags();

  const handleClick = (searchTerm: string) => {
    setAlsoSearched(true);
    dispatchLegacyAnalyticsEvent(ALSO_SEARCHED, {
      pagetype: getAnalyticsPageType(
        route.pageType,
        productListingData.results,
      ),
      term: searchTerm,
    });
    dispatchLegacyAnalyticsEvent(GLOBAL_SEARCH_INTERACTION, {
      searchData: {
        type: "similar search",
        term: route.query["search-term"],
        suggestion: searchTerm,
      },
    });
  };

  const { correction } = productListingData;

  if (!correction) {
    return null;
  }

  const MAX_SIMILAR_SEARCHES = 5;
  const { alternativeSearchSuggestions } = correction;

  if (
    !alternativeSearchSuggestions ||
    !Array.isArray(alternativeSearchSuggestions) ||
    alternativeSearchSuggestions.length === 0
  ) {
    return null;
  }

  return (
    <div
      data-component="SimilarSearches"
      data-testid="similar-search-terms"
      className={styles.terms}
    >
      <p className={styles.subHeading}>
        Similar searches:
        <span className={styles.termsList}>
          {alternativeSearchSuggestions
            .slice(0, MAX_SIMILAR_SEARCHES)
            .map(({ searchTerm }) => (
              <LinkWithoutPrefetch
                href={{
                  pathname: "/search",
                  query: sanitiseQueryParams({ "search-term": searchTerm }),
                }}
                key={searchTerm}
                className={styles.link}
              >
                {isJldsV31Enabled?.value ? (
                  <V31UnderlinedButton
                    tag="span"
                    className={styles.term}
                    data-testid="similar-search-term"
                    onClick={() => handleClick(searchTerm)}
                    labelText={searchTerm}
                  />
                ) : (
                  <DeprecatedUnderlinedButton
                    tag="span"
                    className={styles.term}
                    data-testid="similar-search-term"
                    onClick={() => handleClick(searchTerm)}
                  >
                    {searchTerm}
                  </DeprecatedUnderlinedButton>
                )}
              </LinkWithoutPrefetch>
            ))}
        </span>
      </p>
    </div>
  );
}
